/**
 * Shared locale store factory and translation hook generator.
 * Web and mobile instantiate separate store instances backed by their own storage adapters.
 * Catalogs sourced from packages/locales/ via codegen (scripts/build-locales.mjs → locale-catalogs.generated.ts).
 * ICU MessageFormat engine (intl-messageformat) handles interpolation, plurals, and number/date formatting.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { PersistStorage } from "zustand/middleware";
import IntlMessageFormat from "intl-messageformat";
import { TRANSLATIONS } from "./locale-catalogs.generated";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Locale = "en-TC" | "ht" | "es";
export const LOCALES: Locale[] = ["en-TC", "ht", "es"];
export const LOCALE_LABELS: Record<Locale, string> = {
  "en-TC": "EN",
  ht: "HT",
  es: "ES",
};
export const LOCALE_NAMES: Record<Locale, string> = {
  "en-TC": "English",
  ht: "Kreyòl Ayisyen",
  es: "Español",
};

// ---------------------------------------------------------------------------
// CLDR locale tag mapping — app uses "en-TC"/"ht"/"es" for store/switcher;
// ICU engine needs CLDR tags for correct plural rules + Intl formatting.
// ---------------------------------------------------------------------------

export const CLDR_TAG: Record<Locale, string> = {
  "en-TC": "en",
  ht: "ht",
  es: "es",
};

/**
 * D12 date-localization tag for the shared `format.ts` helpers (`formatDate`,
 * `formatMonth`, `formatEtaLong`, …) — the SINGLE source of truth for the
 * "which locales format via `Intl`" rule, consumed by both apps' `useLocaleTag()`.
 *
 * Returns `undefined` for en-TC so the helpers take their byte-identical English
 * table path (D1): an explicit tag would route en-TC through `Intl`, which renders
 * currency as "US$" and re-orders date parts, breaking the en-TC byte-identity the
 * insight/format snapshot pins enforce. Returns the CLDR tag for es/ht so those
 * localize. Pass the result as the helpers' optional `locale` argument.
 */
export function localeToTag(locale: Locale): string | undefined {
  return locale === "en-TC" ? undefined : CLDR_TAG[locale];
}

// EN_TC reference for the fallback chain
const EN_TC = TRANSLATIONS["en-TC"];

// ---------------------------------------------------------------------------
// ICU formatter cache — memoize compiled IntlMessageFormat instances
// keyed by resolved message string + locale tag to avoid re-parsing on re-render.
// ---------------------------------------------------------------------------

const _fmtCache = new Map<string, IntlMessageFormat>();

function getFormatter(msg: string, localeTag: string): IntlMessageFormat {
  const cacheKey = `${localeTag}\0${msg}`;
  let fmt = _fmtCache.get(cacheKey);
  if (!fmt) {
    fmt = new IntlMessageFormat(msg, localeTag);
    _fmtCache.set(cacheKey, fmt);
  }
  return fmt;
}

// ---------------------------------------------------------------------------
// Store factory
// ---------------------------------------------------------------------------

export interface LocaleState {
  locale: Locale;
  setLocale: (l: Locale) => void;
}

type AnyStorage = PersistStorage<LocaleState> | (() => Storage);

/**
 * Creates a locale store backed by the provided storage adapter.
 * Web: pass `() => localStorage`
 * Mobile: pass `createJSONStorage(() => AsyncStorage)` from zustand/middleware
 */
export function createLocaleStore(storage: AnyStorage) {
  return create<LocaleState>()(
    persist(
      (set) => ({
        locale: "en-TC",
        setLocale: (locale: Locale) => set({ locale }),
      }),
      {
        name: "petci_demo_locale",
        storage: typeof storage === "function"
          ? createJSONStorage<LocaleState>(storage)
          : storage,
      },
    ),
  );
}

// ---------------------------------------------------------------------------
// useT hook factory
// ---------------------------------------------------------------------------

export type TranslateParams = Record<string, string | number | Date>;

/**
 * Returns a hook that exposes a `t(key, params?)` translation function.
 * Pass the store instance created by createLocaleStore.
 */
export function makeUseT(useStore: () => LocaleState) {
  return function useT(): (key: string, params?: TranslateParams) => string {
    const locale = useStore().locale;
    return (key: string, params?: TranslateParams) => {
      const catalog = TRANSLATIONS[locale];
      const msg = catalog[key] ?? EN_TC[key] ?? key;
      const localeTag = CLDR_TAG[locale];

      if (!params || Object.keys(params).length === 0) {
        return msg;
      }

      try {
        const fmt = getFormatter(msg, localeTag);
        const result = fmt.format(params);
        // IntlMessageFormat.format may return string | (string | T)[] — coerce to string
        return typeof result === "string" ? result : String(result);
      } catch {
        // Malformed ICU string — degrade to raw text, never crash render
        if (process.env.NODE_ENV !== "production") {
          console.warn(`[i18n] ICU format error for key "${key}" locale "${locale}"`);
        }
        return msg;
      }
    };
  };
}
